/**
 * «STA GIOCANDO · GUARDA» — le regole della TMA su `liveMatch` (contratto di fronte QSocials r38
 * `fronti/qsocials/r38-quando-gioca-chi-segui.md`, §1-bis «QSocials → TMA»; accettato L-2919 (a), 07/10).
 *
 * Questo file e' PURO (niente `env`, niente React, niente rete): le tre regole decise dal seggio TMA stanno
 * qui e solo qui, e i test le esercitano da qui. Chi mostra «sta giocando» o «Guarda» passa da queste funzioni.
 *
 *  (1) una partita `unlimited: true` vale «sta giocando» SOLO se l'amico e' anche online
 *      (`status !== 'offline'`): una partita senza orologio resta accesa finche' il gameserver risponde, e
 *      senza questa regola un amico uscito dall'app da ore risulterebbe «in partita» (ADR-0208);
 *  (2) un `game` che la TMA non conosce ⇒ «sta giocando» SENZA «Guarda»;
 *  (3) campo assente = `null` = niente: oggi QSocials non serve `liveMatch`, e la TMA degrada, mai inventa.
 */
import type { LiveMatch, PresenceStatus } from '@/types/social';

/** I giochi per cui la TMA sa aprire una visione. Oggi solo il backgammon (`backgammon/live/:id`). */
export type GiocoGuardabile = 'backgammon';

/** L'id di QGammon e' un UUID (stessa forma della regex chiusa di `guarda_bg_` in `lib/startParam.ts`). */
export const UUID_QGAMMON_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const STATI_NOTI: readonly PresenceStatus[] = ['online', 'idle', 'in_tournament', 'watching', 'offline'];

/** Uno `status` che la TMA conosce, o `null`: uno sconosciuto si renderebbe «Online» (`UserProfile.tsx`). */
export function statoNoto(v: unknown): PresenceStatus | null {
  return typeof v === 'string' && (STATI_NOTI as readonly string[]).includes(v) ? (v as PresenceStatus) : null;
}

/**
 * Regola (3): da un valore QUALSIASI (il JSON del server, o l'assenza) a `LiveMatch | null`.
 * Assente, `null`, non-oggetto o privo di `game`/`matchId` stringa non vuota/`unlimited` booleano ⇒ `null`.
 * `since` non si legge a schermo e non si pretende.
 */
export function leggiLiveMatch(v: unknown): LiveMatch | null {
  if (v === null || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (typeof o.game !== 'string' || o.game === '') return null;
  if (typeof o.matchId !== 'string' || o.matchId === '') return null;
  if (typeof o.unlimited !== 'boolean') return null;
  return {
    game: o.game,
    matchId: o.matchId,
    unlimited: o.unlimited,
    ...(typeof o.tournamentId === 'string' && o.tournamentId !== '' ? { tournamentId: o.tournamentId } : {}),
    ...(typeof o.since === 'string' ? { since: o.since } : {}),
  };
}

/**
 * Regole (3) e (1): cio' che la riga/il profilo devono mostrare come «sta giocando», o `null`.
 * `status` e' quello della STESSA risposta (o dello stesso evento) da cui viene `liveMatch`.
 */
export function liveMatchDaMostrare(liveMatch: unknown, status: PresenceStatus | undefined): LiveMatch | null {
  const lm = leggiLiveMatch(liveMatch);
  if (!lm) return null;
  if (lm.unlimited && (status === undefined || status === 'offline')) return null;
  return lm;
}

/** Cosa serve sapere della build e dell'utente per offrire «Guarda». */
export interface ContestoGuarda {
  /** La rotta `backgammon/live/:id` esiste in questa build (`featureBackgammonEnabled` E `featureBackgammonLiveEnabled`). */
  rottaBackgammonLive: boolean;
  /** L'utente ha il ruolo `beta` (la rotta e' dietro `<RoleGate role="beta">`). */
  beta: boolean;
}

/**
 * Regola (2) e il vincolo del dispatch: la rotta di «Guarda», o `null` se un bottone sarebbe un vicolo cieco.
 * ⇒ `null` per un `game` sconosciuto, per un `matchId` che non e' un UUID di QGammon, per la rotta assente
 * in questa build e per un utente non beta. «Sta giocando» si mostra comunque: e' «Guarda» che sparisce.
 */
export function rottaGuarda(lm: LiveMatch, ctx: ContestoGuarda): string | null {
  if (lm.game !== 'backgammon') return null;
  if (!ctx.rottaBackgammonLive || !ctx.beta) return null;
  if (!UUID_QGAMMON_RE.test(lm.matchId)) return null;
  return `/backgammon/live/${lm.matchId}`;
}

/** Un evento del canale personale `presence:friends:<sub>`, validato: `{ type:'presence', userId, status, liveMatch }`. */
export interface EventoPresenza {
  userId: string;
  /** `null` = lo `status` dell'evento non e' uno che la TMA conosce: non si scrive (si tiene quello in cache). */
  status: PresenceStatus | null;
  /** `null` = fine della partita, o campo assente (regola (3)). */
  liveMatch: LiveMatch | null;
}

/** `null` se `data` non e' un evento `presence` con un `userId`. Qualunque altro `type` resta com'e'. */
export function leggiEventoPresenza(data: unknown): EventoPresenza | null {
  if (data === null || typeof data !== 'object') return null;
  const e = data as Record<string, unknown>;
  if (e.type !== 'presence') return null;
  if (typeof e.userId !== 'string' || e.userId === '') return null;
  return { userId: e.userId, status: statoNoto(e.status), liveMatch: leggiLiveMatch(e.liveMatch) };
}
